import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { blogService } from '@/services/blogService';
import { BlogCard } from '@/components/blog/BlogCard';
import { BlogSidebar } from '@/components/blog/BlogSidebar';
import type { BlogPost, BlogCategory } from '@/types/blog';
import { PublicHeader } from '@/components/Layout/PublicHeader';
import { PublicFooter } from '@/components/Layout/PublicFooter';

export function BlogListing() {
  const [searchParams, setSearchParams] = useSearchParams();
  const categorySlug = searchParams.get('category') || undefined;
  const pageParam = parseInt(searchParams.get('page') || '1');

  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(pageParam);
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [recentPosts, setRecentPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeCategoryName, setActiveCategoryName] = useState<string | null>(null);

  useEffect(() => {
    setPage(pageParam);
  }, [pageParam]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [postsResult, categoriesResult, recentResult] = await Promise.all([
          blogService.listPublicPosts({ category: categorySlug, page, pageSize: 9 }),
          blogService.listPublicCategories(),
          blogService.getRecentPosts(5),
        ]);
        setPosts(postsResult.items);
        setTotalPages(postsResult.pagination.totalPages);
        setCategories(categoriesResult);
        setRecentPosts(recentResult);

        if (categorySlug) {
          const found = categoriesResult.find((c) => c.slug === categorySlug);
          setActiveCategoryName(found?.name || null);
        } else {
          setActiveCategoryName(null);
        }
      } catch (err) {
        console.error('Failed to load blog data:', err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [categorySlug, page]);

  useEffect(() => {
    document.title = activeCategoryName
      ? `${activeCategoryName} - Blog | Cascady`
      : 'Blog | Cascady';
    return () => { document.title = 'Cascady'; };
  }, [activeCategoryName]);

  const handlePageChange = (newPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', String(newPage));
    setSearchParams(params);
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <PublicHeader />

      <main className="flex-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Page Title */}
          <div className="mb-10">
            <h1 className="text-4xl font-bold text-gray-900">
              {activeCategoryName ? activeCategoryName : 'Blog'}
            </h1>
            <p className="mt-2 text-lg text-gray-600">
              {activeCategoryName
                ? `Posts in ${activeCategoryName}`
                : 'Insights, tutorials, and updates about AI image generation.'}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-10">
            {/* Main Content */}
            <div>
              {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div
                      key={i}
                      className="rounded-xl border border-gray-200 overflow-hidden animate-pulse"
                    >
                      <div className="aspect-[3/2] bg-gray-200" />
                      <div className="p-5 space-y-3">
                        <div className="h-4 bg-gray-200 rounded w-1/4" />
                        <div className="h-5 bg-gray-200 rounded w-3/4" />
                        <div className="h-3 bg-gray-200 rounded w-1/2" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : posts.length === 0 ? (
                <div className="text-center py-20">
                  <p className="text-lg text-gray-500">No posts found.</p>
                  {categorySlug && (
                    <Link
                      to="/blog"
                      className="inline-block mt-3 text-emerald-600 hover:text-emerald-700 font-medium"
                    >
                      View all posts
                    </Link>
                  )}
                </div>
              ) : (
                <>
                  {/* Featured first post on page 1 */}
                  {page === 1 && !categorySlug && posts.length > 0 && (
                    <div className="mb-8">
                      <BlogCard post={posts[0]} featured />
                    </div>
                  )}

                  {/* Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {(page === 1 && !categorySlug ? posts.slice(1) : posts).map((post) => (
                      <BlogCard key={post.id} post={post} />
                    ))}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-center gap-3 mt-10">
                      <button
                        onClick={() => handlePageChange(page - 1)}
                        disabled={page <= 1}
                        className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none transition-colors"
                      >
                        Previous
                      </button>
                      <span className="text-sm text-gray-500">
                        Page {page} of {totalPages}
                      </span>
                      <button
                        onClick={() => handlePageChange(page + 1)}
                        disabled={page >= totalPages}
                        className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none transition-colors"
                      >
                        Next
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Sidebar */}
            <aside className="hidden lg:block">
              <BlogSidebar
                categories={categories}
                recentPosts={recentPosts}
                activeCategory={categorySlug}
              />
            </aside>
          </div>
        </div>
      </main>

      <PublicFooter />
    </div>
  );
}
